import { useEffect, useState } from 'react';
import { updateOrderPurchaseOrder } from '../../services/orders';
import { updateLsPurchaseOrder, uploadDocumentPurchaseOrder } from '../../services/services';
import { displayErrorNotification, displaySuccessNotification } from '../../utils/displayNudosStandardNotifications';
import { NudosButton, NudosTextInput } from '../NudosComponents';
import BillingPurchaseUploader from '../BillingPurchaseUploader/BillingPurchaseUploader';
import { DOCUMENT_TYPE, IDataDocumentPurchaseOrder } from '../../types/purchaseOrderDocument';
import { isFileValid } from '../../utils/validateFiles';
import { PurcharseOrderConfirmationModal } from 'components/PurcharseOrderConfirmationModal/PurcharseOrderConfirmationModal';
import useResetData from 'hooks/useResetData';
import { BACKOFFICE_MODULES } from 'types/configuration.types';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import './PurchaseOrderSection.scss';

const PurchaseOrderSection = ({
  solutionType,
  solutionId,
  reloadDataCallback,
  currentPurchaseOrder,
  purchaseOrderDocumentUrlOrigin,
  shouldAutomateBilling
}: IpurchaseOrderSection) => {
  const [purchaseOrder, setPurchaseOrder] = useState<string | undefined | null>(currentPurchaseOrder);
  const [loading, setLoading] = useState(false);
  const [purchaseOrderDocumentUrl, setPurchaseOrderDocumentUrl] = useState<string>();
  const [showAcceptConfirmationModal, setShowAcceptConfirmationModal] = useState(false);
  const { resetLSData, resetOrderData } = useResetData();

  const handleResetData = () => {
    switch (solutionType) {
      case BACKOFFICE_MODULES.LOGISTIC_SERVICE:
        resetLSData();
        break;
      case BACKOFFICE_MODULES.ORDER:
        resetOrderData();
        break;
    }
  };

  const updatePurchaseOrder = async () => {
    if (!solutionId || !solutionType || isNaN(+solutionId)) return;
    setLoading(true);
    try {
      switch (solutionType) {
        case BACKOFFICE_MODULES.LOGISTIC_SERVICE:
          const bodyLs = { purchaseOrder: purchaseOrder || null, logisticServiceId: +solutionId };
          await updateLsPurchaseOrder(bodyLs);
          break;
        case BACKOFFICE_MODULES.ORDER:
          const bodyOrder = { purchaseOrder: purchaseOrder || null, orderId: +solutionId };
          await updateOrderPurchaseOrder(bodyOrder);
          break;
      }
      handleResetData();
      displaySuccessNotification({ customJSXMessage: <>Se actualizó la órden de compra exitosamente</> });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseOrderDocumentUpload = async (file: File) => {
    if (!solutionId) return;
    setLoading(true);
    try {
      const dataDocumentPurchaseOrder: IDataDocumentPurchaseOrder = {
        documentType: solutionType === 'logisticService' ? DOCUMENT_TYPE.LOGISTIC_SERVICE_PO : DOCUMENT_TYPE.ORDER_PO,
        entityId: solutionId
      };
      const bodyJson = JSON.stringify(dataDocumentPurchaseOrder);
      const formData = new FormData();
      const isValid = isFileValid(
        file,
        ['application/pdf', 'image/jpeg'],
        5,
        'El documento debe estar en formato PDF o JPG',
        'El documento no debe superar los 5MB'
      );
      if (!isValid) return;
      formData.append('body', bodyJson);
      formData.append('file', file);
      const response = await uploadDocumentPurchaseOrder(formData);
      if (response) {
        displaySuccessNotification({
          customJSXMessage: <>Se subio el archivo exitosamente</>
        });
        handleResetData();
      }
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    // Si ya existe una orden de compra o el input está vacío o no está habilitada la facturación, no se muestra la modal de confirmación y se actualiza la PO
    const executeWithoutConfirmation = currentPurchaseOrder || !purchaseOrder || !shouldAutomateBilling;
    if (executeWithoutConfirmation) {
      handleAcceptUpdatePurchaseOrder();
    } else {
      setShowAcceptConfirmationModal(true);
    }
  }

  const handleCloseAccpetConfirmationModal = () => {
    setShowAcceptConfirmationModal(false);
  }

  const handleAcceptUpdatePurchaseOrder = async () => {
    await updatePurchaseOrder();
    setShowAcceptConfirmationModal(false);
  }

  useEffect(() => {
    if (purchaseOrderDocumentUrlOrigin) {
      setPurchaseOrderDocumentUrl(purchaseOrderDocumentUrlOrigin);
    }
  }, [purchaseOrderDocumentUrlOrigin]);

  return (
    <div className="purchaseOrderSection">
      {showAcceptConfirmationModal && (
        <PurcharseOrderConfirmationModal
          handleCloseAcceptConfirmationModal={handleCloseAccpetConfirmationModal}
          handleAcceptUpdatePurchaseOrder={handleAcceptUpdatePurchaseOrder}
        />
      )}
      <div className="sectionTitle">Orden de compra (PO)</div>
      <div className="purchaseOrderSectionContainer">
        <BillingPurchaseUploader
          uploadProofOfPurchase={handlePurchaseOrderDocumentUpload}
          proofOfPurchase={purchaseOrderDocumentUrl}
          loading={loading}
        />
        <div className="poFormContainer">
          <NudosTextInput
            componentSize="small"
            placeholder="No. de orden de compra (PO)"
            label="No. de orden de compra (PO)"
            isFilled={!!purchaseOrder}
            defaultValue={currentPurchaseOrder || undefined}
            handleChange={setPurchaseOrder}
          />
          <NudosButton
            customClassName="saveButton"
            customWidth="80px"
            customHeight="18px"
            buttonText="Guardar"
            isButtonLoading={loading}
            handleClick={handleOpenModal}
          />
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderSection;

interface IpurchaseOrderSection {
  solutionType: 'order' | 'logisticService';
  solutionId?: number | string;
  reloadDataCallback?: () => void;
  currentPurchaseOrder?: string | null;
  purchaseOrderDocumentUrlOrigin?: string;
  shouldAutomateBilling?: boolean;
}
